import React, { useState } from 'react'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { generateUUID } from '@renderer/utils/idUtils'
import { useNavigate, useParams } from 'react-router-dom'
import ComponentMapper from '@renderer/components/mapper/ComponentMapper'
import { useConfigStore } from '@renderer/stores/configState'
import cogoToast from '@successtar/cogo-toast'
import { Package, RecordingType, Component } from '@renderer/types/configState'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { unifiedCameraUtils } from '@renderer/utils'
import { useCameraStore } from '@renderer/stores/cameraStore'
import { useCreditsStore } from '@renderer/stores/creditsStore'
import { useSubscriptionStore } from '@renderer/stores/subscriptionStore'
import { BackgroundContainer } from '@renderer/components/BackgroundContainer'
import { stateUploader } from '@renderer/utils/pipeline/outputOrchestrator'
import ScaleToFit from '@renderer/components/ScaleToFit'
import { useDeviceOrientation } from '@renderer/hooks/useDeviceOrientation'
import type { ConfigState, DesignOrientation } from '@renderer/types/configState'
import { getDesignCanvasSizeForOrientation } from '@renderer/utils/designCanvasSize'

/** Prefer non-empty lists; `??` would keep an empty oriented branch and hide legacy `components`. */
function coalesceWelcomeComponents(
  welcome: ConfigState['pages']['welcome'] | undefined,
  orientation: DesignOrientation
): Component[] | undefined {
  if (!welcome) return undefined
  const oc = welcome.orientedComponents
  const other: DesignOrientation = orientation === 'portrait' ? 'landscape' : 'portrait'
  const candidates: (Component[] | undefined)[] = [
    oc?.[orientation],
    oc?.[other],
    welcome.components
  ]
  for (const list of candidates) {
    if (Array.isArray(list) && list.length > 0) {
      return list
    }
  }
  return undefined
}

const WelcomePage: React.FC = () => {
  const { setSessionState, logActivity } = useSessionStateStore()
  const { projectId } = useParams()
  const navigate = useNavigate()
  const { config, getPageSettings } = useConfigStore()
  const { selectedSecondaryCamera } = useSettingsStore()
  const welcomeSettings = getPageSettings('welcome')
  const [isSessionStarting, setIsSessionStarting] = useState(false)
  const { isPortrait: deviceIsPortrait } = useDeviceOrientation()
  const designOrientation: DesignOrientation = deviceIsPortrait ? 'portrait' : 'landscape'

  const screenSizeForWelcome = getDesignCanvasSizeForOrientation(config, designOrientation)

  const componentsForWelcome = coalesceWelcomeComponents(
    config?.pages?.welcome,
    designOrientation
  )

  const welcomeW = screenSizeForWelcome.width
  const welcomeH = screenSizeForWelcome.height
  const welcomeShort = Math.min(welcomeW, welcomeH)
  const isCompact = welcomeShort < 768
  const isPortraitLayout = welcomeH > welcomeW

  const startSession = async (mode: 'capture' | 'record'): Promise<void> => {
    if (isSessionStarting) {
      return // Prevent multiple calls
    }
    setIsSessionStarting(true)

    try {
      // Validate camera connection before starting session
      const bypassIds = [
        '67e3f8d12bb1bd3dbad28011',
        '67e3f8e32bb1bd3dbad28012',
        '67e3f8f02bb1bd3dbad28013'
      ]
      const isBypass = bypassIds.includes(projectId || '')

      // For capture mode, require camera connection (unless it's a bypass project)
      if (mode === 'capture') {
        const cameraStore = useCameraStore.getState()
        const { selectedCamera } = useSettingsStore.getState()
        const hasActiveCamera = !!(cameraStore.camera || cameraStore.webcam)

        // Check if camera is actually connected and responding
        if (!hasActiveCamera) {
          // If selectedCamera exists but no active camera, check if it can respond
          if (selectedCamera) {
            const qualityResult = await unifiedCameraUtils.isDeviceResponding('primary')
            if (!qualityResult.isResponding) {
              console.warn('[WELCOME] Camera selected but not responding, showing CameraPrompt')
              // Trigger CameraPrompt via window event
              window.dispatchEvent(new CustomEvent('showCameraPrompt'))
              setIsSessionStarting(false)
              return
            }
          } else {
            // No camera selected at all
            console.warn('[WELCOME] No camera selected for capture mode')
            // Trigger SettingsModal via window event
            window.dispatchEvent(new CustomEvent('showSettingsModal'))
            setIsSessionStarting(false)
            return
          }
        } else {
          // Camera is connected, verify it's responding
          const qualityResult = await unifiedCameraUtils.isDeviceResponding('primary')
          if (!qualityResult.isResponding) {
            console.warn('[WELCOME] Camera connected but not responding, showing CameraPrompt')
            window.dispatchEvent(new CustomEvent('showCameraPrompt'))
            setIsSessionStarting(false)
            return
          }
        }
      }

      // Skip credit validation for bypass projects
      if (isBypass) {
        const sessionId = generateUUID()
        const recordingType = config?.pages?.checkout?.settings?.packages?.find(
          (pkg: Package) =>
            pkg.type === 'recording' ||
            pkg.type === 'strip_live' ||
            pkg.type === 'strip_story' ||
            pkg.type === 'session_recording'
        )?.type as RecordingType

        // Set session ID FIRST before any logs are created
        setSessionState({
          id: sessionId,
          projectId,
          mode,
          timeline: {
            startTime: new Date()
          },
          userPreferences: {
            useGIF: getPageSettings('capture')?.gifEnabled,
            useRecording: getPageSettings('capture')?.recordingEnabled,
            useVideo: getPageSettings('record')?.enabled,
            recordingType
          },
          galleryKey: `${9999999999999 - Date.now()}-${sessionId}`
        })

        // Now log activity with the session ID set
        logActivity(`Session started in ${mode} mode.`)

        const filterSettings = getPageSettings('filter')
        const applyAfterCapture = filterSettings?.applyAfterCapture || false
        const captureMode = config?.pages.capture?.settings?.mode || 'standard'
        const frameEnabled = config?.pages.frame?.settings?.enabled || false
        
        let nextPage: string
        if (welcomeSettings?.startWithPayment) {
          nextPage = 'checkout'
        } else {
          if (applyAfterCapture) {
            // Filter appears after capture - go to Frame (if frame-based) or Capture (if standard)
            if (captureMode === 'frame-based' && frameEnabled) {
              nextPage = 'frame'
            } else {
              nextPage = 'capture'
            }
          } else {
            // Filter appears before capture - always go to Filter first
            nextPage = 'filter'
          }
        }
        if (selectedSecondaryCamera) {
          nextPage = 'camera'
        }

        // Delay session upload to allow more logs to be collected (pass sessionId so overlap is safe)
        setTimeout(() => {
          stateUploader.uploadSessionState(sessionId)
        }, 5000)

        navigate(`/${projectId}/${nextPage}`)
        return
      }
      // Credit deduction is now handled in Capture.tsx when capture session starts
      // This ensures customers only pay when they actually start capturing.
      //
      // P1-1: pre-flight entitlement check using *cached* credits +
      // subscription state — synchronous, no spinner, no network round-trip.
      // We fire-and-forget a background refresh so View.tsx's reactive
      // effect on (isSubscriptionValid, credits) can still surface
      // NoCreditsModal if the server later reveals the cache was stale.
      // The check exists purely to spare an obviously-out-of-credits
      // operator from walking the customer through the whole flow only
      // to hit a 403 at upload time.
      useCreditsStore
        .getState()
        .fetchSubscriptionAndCredits()
        .catch((preflightErr) => {
          console.warn('[WELCOME] Background subscription/credits refresh failed', preflightErr)
        })
      const cachedCredits = useCreditsStore.getState().credits
      const cachedSubscriptionValid = useSubscriptionStore.getState().isValid
      const hasUsableEntitlement =
        cachedSubscriptionValid ||
        cachedCredits === 'Unlimited' ||
        (typeof cachedCredits === 'number' && cachedCredits > 0)
      if (!hasUsableEntitlement) {
        console.warn('[WELCOME] Pre-flight check blocked session start', {
          credits: cachedCredits,
          subscriptionValid: cachedSubscriptionValid
        })
        cogoToast.error(
          'No credits available. Please contact the operator to top up or activate a subscription.',
          { hideAfter: 6 }
        )
        return
      }

      const sessionId = generateUUID()

      // Get recording package to determine type
      const recordingType = config?.pages?.checkout?.settings?.packages?.find(
        (pkg: Package) =>
          pkg.type === 'recording' ||
          pkg.type === 'strip_live' ||
          pkg.type === 'strip_story' ||
          pkg.type === 'session_recording'
      )?.type as RecordingType

      // Set session ID FIRST before any logs are created
      setSessionState({
        id: sessionId,
        projectId,
        mode,
        timeline: {
          startTime: new Date()
        },
        userPreferences: {
          useGIF: getPageSettings('capture')?.gifEnabled,
          useRecording: getPageSettings('capture')?.recordingEnabled,
          useVideo: getPageSettings('record')?.enabled,
          recordingType
        },
        gallery: {
          id: `${9999999999999 - Date.now()}-${sessionId}`
        }
      })

      // Now log activity with the session ID set
      logActivity(`Session started in ${mode} mode.`)

      const frameEnabled = config?.pages.frame?.settings?.enabled || false
      const filterSettings = getPageSettings('filter')
      const applyAfterCapture = filterSettings?.applyAfterCapture || false

      let nextPage: string
      
      if (welcomeSettings?.startWithPayment) {
        // If startWithPayment is enabled, always go to checkout first
        nextPage = 'checkout'
      } else {
        if (applyAfterCapture) {
          if (frameEnabled) {
            nextPage = 'frame'
          } else {
            nextPage = 'capture'
          }
        } else {
          nextPage = 'filter'
        }
      }

      // Check for secondary camera - always go to camera if secondary camera exists
      if (
        selectedSecondaryCamera &&
        (await unifiedCameraUtils.isDeviceResponding('secondary'))
      ) {
        nextPage = 'camera'
      }

      // Upload session state after a delay so we have more than "session started" (logs, etc.)
      // Avoids uploading an empty/minimal session that can appear as duplicate or incomplete
      setTimeout(() => {
        stateUploader.uploadSessionState(sessionId)
      }, 5000)

      navigate(`/${projectId}/${nextPage}`)
    } catch (error) {
      console.error('Error starting session:', error)
      cogoToast.error('Failed to check credits. Please try again.')
    } finally {
      setIsSessionStarting(false)
    }
  }

  // Handle button actions from ComponentMapper
  const handleButtonAction = async (component: Component): Promise<void> => {
    if (!component.buttonAction) return
    const captureMode = config?.pages.capture?.settings?.mode || 'standard'
    const frameEnabled = config?.pages.frame?.settings?.enabled || false
    const filterSettings = getPageSettings('filter')
    const applyAfterCapture = filterSettings?.applyAfterCapture || false

    let nextPage: string
    
    if (welcomeSettings?.startWithPayment) {
      // If startWithPayment is enabled, always go to checkout first
      nextPage = 'checkout'
    } else {
      // If applyAfterCapture is false, Filter appears BEFORE capture - go to Filter
      // If applyAfterCapture is true, Filter appears AFTER capture - go to Frame/Capture
      if (applyAfterCapture) {
        // Filter appears after capture - go to Frame (if frame-based) or Capture (if standard)
        if (captureMode === 'frame-based' && frameEnabled) {
          nextPage = 'frame'
        } else {
          // Standard mode: go directly to capture (skip filter before capture)
          nextPage = 'capture'
        }
      } else {
        // Filter appears before capture - always go to Filter first
        nextPage = 'filter'
      }
    }

    // Check for secondary camera - always go to camera if secondary camera exists
    if (
      selectedSecondaryCamera &&
      (await unifiedCameraUtils.isDeviceResponding('secondary'))
    ) {
      nextPage = 'camera'
    }
    logActivity(`User clicked on "${component.text}" button`, {
      to: nextPage
    })
    switch (component.buttonAction) {
      case 'capture':
        startSession('capture')
        break
      case 'record':
        startSession('record')
        break
      default:
        console.warn('Unknown button action:', component.buttonAction)
    }
  }

  const welcomeBg = config?.theme?.backgroundColor || '#F8FAFC'

  const welcomeBackdrop = (
    <div className="h-full w-full" aria-hidden>
      <BackgroundContainer className="relative h-full w-full overflow-hidden">
        <ComponentMapper
          components={componentsForWelcome}
          designLayout={{ isCompact, isPortrait: isPortraitLayout }}
          variant="backdrop"
        />
      </BackgroundContainer>
    </div>
  )

  return (
    <div className="flex h-full min-h-0 w-full flex-1 flex-col">
      <ScaleToFit
        screenSize={screenSizeForWelcome}
        backgroundColor={welcomeBg}
        backdropChildren={welcomeBackdrop}
        blurBackgroundColor={welcomeBg}
      >
        <BackgroundContainer className="relative overflow-hidden" data-page="welcome">
          <ComponentMapper
            components={componentsForWelcome}
            onButtonAction={handleButtonAction}
            designLayout={{ isCompact, isPortrait: isPortraitLayout }}
          />
        </BackgroundContainer>
      </ScaleToFit>
    </div>
  )
}

export default WelcomePage
